import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  initializeFirestore,
  getFirestore,
  setLogLevel,
  Firestore,
} from 'firebase/firestore';
import firebaseConfigJson from '../firebase-applet-config.json';

// Configure Firestore log level to silent to prevent internal transient connection retry logs
try {
  setLogLevel('silent');
} catch {}

export const firebaseConfig = {
  projectId: firebaseConfigJson.projectId || 'gen-lang-client-0247227503',
  appId: firebaseConfigJson.appId || '1:692704960518:web:27bdf9e539fd8b54a0e387',
  apiKey: firebaseConfigJson.apiKey || 'AIzaSyAgrsblp4nCRqt6RT-Uq3sTvC3NFjSIX38',
  authDomain: firebaseConfigJson.authDomain || 'gen-lang-client-0247227503.firebaseapp.com',
  storageBucket: firebaseConfigJson.storageBucket || 'gen-lang-client-0247227503.firebasestorage.app',
  messagingSenderId: firebaseConfigJson.messagingSenderId || '692704960518',
};

const databaseId = firebaseConfigJson.firestoreDatabaseId || '(default)';

// Initialize Firebase App
export const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// Initialize Auth
export const auth = getAuth(app);

// Initialize Firestore using experimentalAutoDetectLongPolling for optimal browser and proxy transport
const targetDbId = databaseId && databaseId !== '(default)' ? databaseId : undefined;

let firestoreInstance: Firestore;
try {
  firestoreInstance = initializeFirestore(
    app,
    {
      experimentalAutoDetectLongPolling: true,
    },
    targetDbId
  );
} catch {
  // If already initialized or fails, fallback cleanly
  firestoreInstance = targetDbId ? getFirestore(app, targetDbId) : getFirestore(app);
}

export const db = firestoreInstance;

// Optional Firebase App Check initialization
if (typeof window !== 'undefined' && (window as any).__FIREBASE_APP_CHECK_KEY__) {
  import('firebase/app-check').then(({ initializeAppCheck, ReCaptchaEnterpriseProvider }) => {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider((window as any).__FIREBASE_APP_CHECK_KEY__),
        isTokenAutoRefreshEnabled: true,
      });
    } catch {}
  }).catch(() => {});
}


// Operation Types for error tracing according to skill specifications
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errMessage = error instanceof Error ? error.message : String(error);
  const errInfo: FirestoreErrorInfo = {
    error: errMessage,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

